// =============================================================================
// mockNotesToHankResponse.ts — Mock response generator for NTH (web port)
// Gated by featureFlag("mockNotesToHank").
// =============================================================================

import { v4 as uuidv4 } from 'uuid';
import type {
  NoteMessage,
  QuestionObject,
  ActionButton,
} from './NotesToHankTypes';

// ---------------------------------------------------------------------------
// Random text pools
// ---------------------------------------------------------------------------

const RESPONSE_TEXTS = [
  "I've reviewed the property details. Here's what I found based on the photos and information you provided.",
  'Great question! Let me walk you through the key points for this deal.',
  "Based on the inspection data, I'd recommend focusing on the roof and siding first.",
  'The property looks like a good candidate. Here are some next steps to consider.',
  "I've analyzed the photos you sent. There are a few areas that need attention.",
  'Thanks for the update. Let me adjust my recommendations accordingly.',
  "Looking at the damage patterns, this appears to be wind-related. Here's my assessment.",
  "I've compared this with similar properties in the area. Here's what stands out.",
  'Good call on that observation. Let me dig deeper into those details.',
  "The measurements look consistent with what I'd expect. Here's a summary.",
];

const QUESTION_TEXTS = [
  'What type of damage are you seeing?',
  'Which area would you like me to focus on?',
  'How would you like to proceed?',
  "What's the homeowner's primary concern?",
  'Which material is the current roof?',
];

const ANSWER_POOLS = [
  ['Wind damage', 'Hail damage', 'Water damage'],
  ['Roof', 'Siding', 'Gutters'],
  ['Generate report', 'Continue inspection', 'Schedule follow-up'],
  ['Cost', 'Timeline', 'Quality'],
  ['Asphalt shingles', 'Metal', 'Tile'],
];

const ACTION_LABELS = [
  'Generate Report',
  'Show Summary',
  'View Photos',
  'Add Notes',
  'Schedule Appointment',
  'Share with Team',
  'Mark Complete',
  'Request Review',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function maybeGenerateQuestions(): QuestionObject[] | undefined {
  if (Math.random() > 0.4) {
    return undefined;
  }

  const count = randomInt(1, 2);
  const questions: QuestionObject[] = [];

  for (let i = 0; i < count; i++) {
    const qIdx = randomInt(0, QUESTION_TEXTS.length - 1);
    const answerCount = randomInt(1, 3);
    const pool = ANSWER_POOLS[qIdx] ?? ANSWER_POOLS[0];

    questions.push({
      id: uuidv4(),
      question: QUESTION_TEXTS[qIdx],
      answers: pool.slice(0, answerCount).map((text) => ({
        id: uuidv4(),
        text,
      })),
    });
  }

  return questions;
}

function maybeGenerateActionButtons(): ActionButton[] | undefined {
  if (Math.random() > 0.3) {
    return undefined;
  }

  const count = randomInt(1, 3);
  const shuffled = [...ACTION_LABELS].sort(() => Math.random() - 0.5);

  return shuffled.slice(0, count).map((label) => ({
    id: uuidv4(),
    label,
  }));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function generateMockResponse(outbound: NoteMessage): NoteMessage {
  if (outbound.event_type === 'initial') {
    return {
      type: 'note_message',
      event_type: 'response',
      deal_id: outbound.deal_id,
      text: "Hi! I'm Hank, your AI assistant. How can I help you with this deal today?",
      action_buttons: [
        { id: uuidv4(), label: 'Guide Me', clientCommand: 'hank' },
        { id: uuidv4(), label: 'Listen to Me', clientCommand: 'manual' },
        {
          id: uuidv4(),
          label: 'Use Existing Pictures',
          clientCommand: 'upload',
        },
        {
          id: uuidv4(),
          label: 'Order Xactimate Estimate',
          clientCommand: 'orderEstimate',
        },
        { id: uuidv4(), label: 'Submit Claim', clientCommand: 'submitClaim' },
      ],
      timestamp: Date.now(),
    };
  }

  const response: NoteMessage = {
    type: 'note_message',
    event_type: 'response',
    deal_id: outbound.deal_id,
    text: pick(RESPONSE_TEXTS),
    timestamp: Date.now(),
  };

  const questions = maybeGenerateQuestions();
  if (questions) {
    response.questions = questions;
  }

  const buttons = maybeGenerateActionButtons();
  if (buttons) {
    response.action_buttons = buttons;
  }

  return response;
}
